/**
 * FileCard — one file an Ion Next sandbox run produced (`sandbox_file` tool
 * card, artifact_type "file", backend native/files.py).
 *
 * The file is never rendered: no <img src>, no iframe, no object, not even
 * for a PNG. Every byte of it (name, type, content) was chosen by model code,
 * so the only action is a download: authenticatedFetch → Blob (re-typed as
 * application/octet-stream so the browser cannot sniff it into a document)
 * → object URL → <a download> with a sanitised name → revoke.
 */
import { useState } from 'preact/hooks';
import { downloadIonNextFile } from '../../../services/adminService.js';

const MAX_NAME = 128;
const REVOKE_AFTER_MS = 10_000;

/** A download name with no path, no control or reserved characters. */
export function sanitizeDownloadName(raw) {
  let name = String(raw || '');
  name = name.split(/[/\\]/).pop() || '';
  // Control chars (C0, DEL, C1) and bidi / zero-width format characters.
  name = name.replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩]/g, '');
  name = name.replace(/[<>:"|?*;`]+/g, '_');
  name = name.replace(/\s+/g, ' ').replace(/^[ .]+|[ .]+$/g, '');
  if (name.length > MAX_NAME) {
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 ? name.slice(dot) : '';
    name = ext.length > 0 && ext.length <= 11
      ? name.slice(0, MAX_NAME - ext.length) + ext
      : name.slice(0, MAX_NAME);
  }
  return name || 'download';
}

export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

/** Hand `blob` to the browser as a download named `filename`. */
export function saveBlob(blob, filename) {
  const opaque = new Blob([blob], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(opaque);
  const a = document.createElement('a');
  a.href = url;
  a.download = sanitizeDownloadName(filename);
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    // Later, not right away: some browsers start reading the blob only after
    // the click handler returns, and a large file may still be starting.
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  }
}

/**
 * @param {object} props
 * @param {object|null} props.artifact — the `file` artifact ({file_id, filename, media_type, size_bytes})
 * @param {object|null} [props.error] — the skip note ({code, message}) when the file was not stored
 * @param {'mine'|'all'} [props.scope] — 'all' when the file is in another admin's conversation
 */
export function FileCard({ artifact, error, scope = 'mine' }) {
  const [state, setState] = useState({ busy: false, error: null });

  if (!artifact) {
    return (
      <div className="ion-next-file-card is-skipped" role="note">
        <span className="ion-next-file-card__icon" aria-hidden="true">⊘</span>
        <div className="ion-next-file-card__meta">
          <span className="ion-next-file-card__name">File not stored</span>
          <span className="ion-next-file-card__sub">
            {error?.message || error?.code || 'The sandbox file was skipped.'}
          </span>
        </div>
      </div>
    );
  }

  const name = sanitizeDownloadName(artifact.filename);
  const handleDownload = async () => {
    setState({ busy: true, error: null });
    try {
      const blob = await downloadIonNextFile(artifact.file_id, { scope });
      saveBlob(blob, name);
      setState({ busy: false, error: null });
    } catch (e) {
      setState({ busy: false, error: e?.message || 'Download failed' });
    }
  };

  return (
    <div className="ion-next-file-card">
      <span className="ion-next-file-card__icon" aria-hidden="true">⇩</span>
      <div className="ion-next-file-card__meta">
        <span className="ion-next-file-card__name" title={name}>{name}</span>
        <span className="ion-next-file-card__sub">
          {artifact.media_type || 'application/octet-stream'} · {formatBytes(artifact.size_bytes)}
        </span>
        {state.error && <span className="ion-next-file-card__error">{state.error}</span>}
      </div>
      <button
        type="button"
        className="admin-ai-btn admin-ai-btn--ghost"
        onClick={handleDownload}
        disabled={state.busy}
      >
        {state.busy ? 'Downloading…' : 'Download'}
      </button>
    </div>
  );
}
